// Lagerkorreksjon Service - Bruker eksisterende StcTransOrder endepunkter
import { AuthService } from './authService';
import { extractApiError } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface InventoryCorrectionRequest {
  itemNo: string;
  locationName: string;
  quantity: number;
  warehouse: string;
  transType: number; // 1=ut av lager, 2=inn på lager
  reasonCode: number;
  description1?: string;
  itemBatchNo?: string;
  itemBatchExpDate?: Date;
  itemSerialNo?: string;
  itemType?: number;
  internalOnly?: boolean; // true = kun intern korreksjon, sendes ikke til ERP
}

export interface InventoryCorrectionLine {
  lineID: number;
  itemNo: string;
  locationName: string;
  quantity: number;
  warehouse: string;
  transType: number;
  transTypeText: string;
  reasonCode: number;
  description1: string;
  itemBatchNo?: string;
  itemBatchExpDate?: Date;
  itemSerialNo?: string;
  transDt: Date;
  lineStatus: number;
  statusText: string;
}

export interface InventoryCorrectionSession {
  batchID: string;
  operatorID: string;
  description1: string;
  orderStatus: number;
  statusText: string;
  warehouse: string;
  orderDate: Date;
  transactionCount: number;
  lines: InventoryCorrectionLine[];
}

export interface ReasonCode {
  code: number;
  description: string;
  reasonType?: number | null;  // 1 = ut av lager, 2 = inn i lager
  externalID?: string | null;  // ERP-kode for eksport
}

export interface ItemValidationResult {
  valid: boolean;
  itemNo: string;
  description1?: string;
  itemType?: number;
  itemUnitTxt?: string;
  errorMessage?: string;
}

export interface LocationValidationResult {
  valid: boolean;
  locationName: string;
  warehouseID?: string;
  errorMessage?: string;
}

// Interfaces for lagerbeholdning (samme som i warehouse transfer)
export interface ItemLocationInfo {
  itemNo: string;
  description: string;
  locationName: string;
  warehouseID: string;
  availableQuantity: number;
  totalQuantity: number;
  reservedQuantity: number;
  binCodeFixed: boolean;
  binCodePrimary: boolean;
  lastMovementDate?: Date;
}

export interface LocationItemInfo {
  locationName: string;
  warehouseID: string;
  itemNo: string;
  description: string;
  itemType?: number;
  availableQuantity: number;
  totalQuantity: number;
  reservedQuantity: number;
  binCodeFixed: boolean;
  binCodePrimary: boolean;
  lastMovementDate?: Date;
}

export interface InventoryCorrectionFormData {
  scannedCode: string;
  quantity: string;
  transType: number;
  reasonCode: number;
  internalOnly: boolean;
  isScanning: boolean;
  validationMessage: string;
  validationType: 'success' | 'error' | '';
}

/**
 * Strukturert detalj fra backend-feil som peker på en konkret korreksjonslinje
 * (f.eks. INSUFFICIENT_STOCK_FOR_CORRECTION fra StcTransOrderService). Se
 * ExceptionHandlingMiddleware -> ErrorResponse.details.
 */
export interface CorrectionErrorDetails {
  itemNo?: string;
  binCode?: string;
  warehouseID?: string;
  currentQuantity?: number;
  requestedDeduction?: number;
}

/**
 * Feil kastet av completeCorrectionSession. Bærer server-detaljen videre slik at
 * UI kan henvise til hvilken vare/linje som blokkerte fullføringen.
 */
export interface CompleteCorrectionError extends Error {
  errorCode?: string;
  details?: CorrectionErrorDetails | null;
}

class InventoryCorrectionService {
  private baseUrl = API_BASE_URL;

  // Cache for årsakskoder hentet fra API
  private reasonCodesCache: ReasonCode[] | null = null;

  // Hent årsakskoder fra API (ReasonCode-tabellen)
  async fetchReasonCodes(): Promise<ReasonCode[]> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/reason-codes/current`);
      if (!response.ok) {
        console.warn('Failed to fetch reason codes from API, status:', response.status);
        return [];
      }
      const data = await response.json();
      // Map fra ReasonCodeDto til ReasonCode interface
      const codes: ReasonCode[] = data.map((rc: any) => ({
        code: rc.reasonCodeNo,
        description: rc.reasonDescription || `Kode ${rc.reasonCodeNo}`,
        reasonType: rc.reasonType ?? null,
        externalID: rc.externalID ?? null
      }));
      this.reasonCodesCache = codes;
      return codes;
    } catch (error) {
      console.error('Error fetching reason codes from API:', error);
      return [];
    }
  }

  // Synkron tilgang til cached årsakskoder (for getReasonCodeDescription)
  getReasonCodes(): ReasonCode[] {
    return this.reasonCodesCache || [];
  }

  // Få aktiv korreksjons-session for operatør (top 1)
  async getActiveCorrectionSession(operatorId: string): Promise<InventoryCorrectionSession | null> {
    try {
      // Hent alle correction sessions for operatøren (OrderType 3 = inventory correction, Status 20 = Under arbeid)
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/operator/${operatorId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch correction sessions');
      }
      const sessions = await response.json();
      
      // Finn aktiv correction session (status 20 og OrderType 3)
      const activeSessions = sessions.filter((s: any) => 
        s.orderType === 3 && s.orderStatus === 20
      );
      
      if (activeSessions.length === 0) {
        return null;
      }

      // Ta den nyeste
      const activeSession = activeSessions.sort((a: any, b: any) => 
        new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime()
      )[0];

      // Hent detaljer med transakcsjoner
      const detailResponse = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/${activeSession.batchID}/details`);
      if (!detailResponse.ok) {
        throw new Error('Failed to fetch session details');
      }
      const details = await detailResponse.json();

      return this.mapToCorrectionSession(details);
    } catch (error) {
      console.error('Error getting active correction session:', error);
      return null;
    }
  }

  // Opprett ny korreksjons-session
  async createCorrectionSession(operatorId: string, warehouse: string): Promise<InventoryCorrectionSession> {
    try {
      const now = new Date();
      const batchID = `CORR_${now.toISOString().slice(0, 19).replace(/[-:T]/g, '').slice(0, 14)}_${operatorId}`;
      
      const request = {
        batchID,
        operatorID: operatorId,
        orderDate: now,
        description1: `Lagerkorreksjon ${now.toLocaleDateString('no-NO')} ${now.toLocaleTimeString('no-NO')} ${operatorId}`,
        orderStatus: 20, // Under arbeid
        profileID: `${AuthService.getUserDataFromToken()?.customerNo ?? ""}${operatorId}`, // Fallback — backend slår opp faktisk Profile.ProfileID og overstyrer
        orderType: 3, // Inventory correction (ikke 2 som er warehouse transfer)
        warehouse,
        lineTypeOrigin: 0 // Standard
        // StatID genereres automatisk av backend (erstatter INSERT trigger)
      };

      const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(extractApiError(error, response, 'Failed to create correction session'));
      }
      
      // Return session med tomme linjer
      const responseData = await response.json();
      return {
        batchID: responseData.batchID,
        operatorID: responseData.operatorID,
        description1: responseData.description1,
        orderStatus: responseData.orderStatus,
        statusText: responseData.statusText,
        warehouse: responseData.warehouse,
        orderDate: new Date(responseData.orderDate),
        transactionCount: 0,
        lines: []
      };
    } catch (error) {
      console.error('Error creating correction session:', error);
      throw new Error('Kunne ikke opprette korreksjonssession');
    }
  }

  // Valider varenummer (samme som warehouse transfer)
  async validateItem(itemNo: string): Promise<ItemValidationResult> {
    try {
      console.log('🔍 [validateItem] Validating item:', itemNo);
      const startTime = performance.now();

      // TW 36320099: samlet, EKSAKT oppslag først — matcher varens hovedstrekkode OG alternative
      // strekkoder (ItemUnits), med GTIN-13/14-varianter håndtert av serveren. Kun eksakte treff,
      // så delstreng-fella ved lokasjonskoder gjelder ikke her.
      const resolveResponse = await AuthService.authenticatedGet(`${this.baseUrl}/items/resolve-barcode/current?barcode=${encodeURIComponent(itemNo)}`);
      if (resolveResponse.ok) {
        const resolveData = await resolveResponse.json();
        if (resolveData && resolveData.length > 0) {
          console.log('✅ [validateItem] Item found via resolve (Items/ItemUnits):', resolveData[0].itemNo);
          return {
            valid: true,
            itemNo: resolveData[0].itemNo,
            description1: resolveData[0].description1,
            itemType: resolveData[0].itemType
          };
        }
        console.log('🔍 [validateItem] Resolve (Items/ItemUnits) ga ingen treff — fortsetter til eldre oppslag...');
      }

      // Try barcode search first
      const barcodeResponse = await AuthService.authenticatedGet(`${this.baseUrl}/items/barcode/current?barcode=${encodeURIComponent(itemNo)}`);

      const barcodeEndTime = performance.now();
      console.log(`⏱️ [validateItem] Barcode search took ${Math.round(barcodeEndTime - startTime)}ms, status: ${barcodeResponse.status}`);

      if (barcodeResponse.ok) {
        const barcodeData = await barcodeResponse.json();
        if (barcodeData && barcodeData.length > 0) {
          // Only accept exact barcode match — partial LIKE matches should fall through
          // to item search which has serial/batch priority ordering
          const exactBarcodeMatch = barcodeData.find((i: any) =>
            i.itemBarCode && i.itemBarCode.toLowerCase() === itemNo.toLowerCase()
          );
          if (exactBarcodeMatch) {
            console.log('✅ [validateItem] Item found via exact barcode:', exactBarcodeMatch.itemNo);
            return {
              valid: true,
              itemNo: exactBarcodeMatch.itemNo,
              description1: exactBarcodeMatch.description1,
              itemType: exactBarcodeMatch.itemType,
              itemUnitTxt: exactBarcodeMatch.itemUnitTxt
            };
          }
          console.log('🔍 [validateItem] Barcode LIKE match found but no exact match, continuing to item search...');
        }
      }

      // Barcode didn't match - try direct item number search using OPTIMIZED search endpoint
      console.log('🔍 [validateItem] Barcode not found, trying direct item number search...');
      const itemSearchStart = performance.now();
      const itemResponse = await AuthService.authenticatedGet(`${this.baseUrl}/items/search/current?searchTerm=${encodeURIComponent(itemNo)}`);

      const itemSearchEnd = performance.now();
      console.log(`⏱️ [validateItem] Item search took ${Math.round(itemSearchEnd - itemSearchStart)}ms, status: ${itemResponse.status}`);

      if (!itemResponse.ok) {
        return {
          valid: false,
          itemNo,
          errorMessage: `Vare "${itemNo}" ble ikke funnet`
        };
      }

      const itemData = await itemResponse.json();
      if (itemData && itemData.length > 0) {
        // Kun EKSAKT itemNo-treff godtas ved skann-validering. Vare-søket er et LIKE/delstreng-
        // søk (også mot strekkode), så et delstreff skal IKKE tolkes som vare — ellers matcher
        // en lokasjon som er delstreng av en EAN (f.eks. lokasjon "501204" → strekkode
        // "7073065012045") feilaktig som vare, og skanningen rutes aldri til lokasjons-
        // validering. Fri-tekst-delsøk gjøres via "Søk vare"-modalen, ikke her.
        const exactMatch = itemData.find((i: any) => i.itemNo.toLowerCase() === itemNo.toLowerCase());
        if (exactMatch) {
          console.log('✅ [validateItem] Item found via exact item search:', exactMatch.itemNo);
          return {
            valid: true,
            itemNo: exactMatch.itemNo,
            description1: exactMatch.description1,
            itemType: exactMatch.itemType,
            itemUnitTxt: exactMatch.itemUnitTxt
          };
        }
        console.log('🔍 [validateItem] Item search ga kun delstreff — tolkes ikke som vare, faller gjennom til lokasjon');
      }

      console.log('❌ [validateItem] Item not found (no exact match)');
      return {
        valid: false,
        itemNo,
        errorMessage: `Vare "${itemNo}" ble ikke funnet`
      };
    } catch (error) {
      console.error('❌ [validateItem] Error validating item:', error);
      return {
        valid: false,
        itemNo,
        errorMessage: 'Feil ved validering av vare'
      };
    }
  }

  // Valider lokasjon (samme som warehouse transfer)
  async validateLocation(locationName: string): Promise<LocationValidationResult> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/stc-bins/current`);
      if (!response.ok) {
        throw new Error('Failed to validate location');
      }
      const bins = await response.json();
      
      const foundBin = bins.find((bin: any) => bin.binCode === locationName);
      
      if (foundBin) {
        return {
          valid: true,
          locationName: foundBin.binCode,
          warehouseID: foundBin.warehouseID
        };
      }

      return {
        valid: false,
        locationName,
        errorMessage: `Lokasjon "${locationName}" ble ikke funnet`
      };
    } catch (error) {
      console.error('Error validating location:', error);
      return {
        valid: false,
        locationName,
        errorMessage: 'Feil ved validering av lokasjon'
      };
    }
  }

  // Hent lokasjoner for en vare (samme som warehouse transfer)
  async getLocationsForItem(itemNo: string, warehouseId?: string | null): Promise<ItemLocationInfo[]> {
    try {
      const whParam = warehouseId ? `?warehouseId=${encodeURIComponent(warehouseId)}` : '';
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/data-inspection/itembins/item/${encodeURIComponent(itemNo)}${whParam}`);
      if (!response.ok) {
        throw new Error('Failed to get locations for item');
      }
      
      const itemBins = await response.json();

      // Aggreger per BinCode+WarehouseID — batch-varer har flere ItemBins-rader per lokasjon
      const locationMap = new Map<string, ItemLocationInfo>();
      for (const bin of itemBins) {
        const key = `${bin.binCode}|${bin.warehouseID}`;
        const existing = locationMap.get(key);
        if (existing) {
          existing.availableQuantity += (bin.quantityAvailable || 0);
          existing.totalQuantity += (bin.quantity || 0);
          existing.reservedQuantity += (bin.quantityReserved || 0);
          if (bin.binCodePrimary) existing.binCodePrimary = true;
          if (bin.binCodeFixed) existing.binCodeFixed = true;
          if (bin.lastMovementDate) {
            const newDate = new Date(bin.lastMovementDate);
            if (!existing.lastMovementDate || newDate > existing.lastMovementDate) {
              existing.lastMovementDate = newDate;
            }
          }
        } else {
          locationMap.set(key, {
            itemNo: bin.itemNo,
            description: bin.itemDescription || '',
            locationName: bin.binCode,
            warehouseID: bin.warehouseID,
            availableQuantity: bin.quantityAvailable || 0,
            totalQuantity: bin.quantity || 0,
            reservedQuantity: bin.quantityReserved || 0,
            binCodeFixed: bin.binCodeFixed || false,
            binCodePrimary: bin.binCodePrimary || false,
            lastMovementDate: bin.lastMovementDate ? new Date(bin.lastMovementDate) : undefined
          });
        }
      }
      return Array.from(locationMap.values()); // Ikke filtrer på tilgjengelig antall for korreksjon - kan korrigere negative beholdninger også
    } catch (error) {
      console.error('Error getting locations for item:', error);
      return [];
    }
  }

  // Hent varer på en lokasjon (samme som warehouse transfer)
  async getItemsAtLocation(locationName: string): Promise<LocationItemInfo[]> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/data-inspection/itembins/by-bin/${encodeURIComponent(locationName)}`);
      if (!response.ok) {
        throw new Error('Failed to get items at location');
      }
      
      const itemBins = await response.json();
      
      return itemBins.map((bin: any): LocationItemInfo => ({
        locationName: bin.binCode,
        warehouseID: bin.warehouseID,
        itemNo: bin.itemNo,
        description: bin.itemDescription || '',
        itemType: bin.itemType,
        availableQuantity: bin.quantityAvailable || 0,
        totalQuantity: bin.quantity || 0,
        reservedQuantity: bin.quantityReserved || 0,
        binCodeFixed: bin.binCodeFixed || false,
        binCodePrimary: bin.binCodePrimary || false,
        lastMovementDate: bin.lastMovementDate ? new Date(bin.lastMovementDate) : undefined
      })); // Ikke filtrer på tilgjengelig antall for korreksjon
    } catch (error) {
      console.error('Error getting items at location:', error);
      return [];
    }
  }

  // Legg til korreksjonslinje
  async addCorrectionLine(batchId: string, correctionData: InventoryCorrectionRequest): Promise<InventoryCorrectionLine> {
    try {
      // Hent operatorID fra batchID (format: "CORR_YYYYMMDDHHMMSS_OperatorID")
      const operatorID = batchId.split('_').slice(2).join('_');
      
      const request = {
        batchID: batchId,
        lineType: 3, // Lagerkorreksjon (legacy: LineType 3)
        orderNo: 0,
        lineNo: 0,
        locationName: correctionData.locationName, // Korreksjons-lokasjon
        quantity: correctionData.quantity,
        transDt: new Date(),
        toLocationName: null, // Ingen til-lokasjon for korreksjon
        warehouse: correctionData.warehouse,
        toWarehouse: correctionData.warehouse, // Samme warehouse
        operatorID: operatorID,
        profileID: `${AuthService.getUserDataFromToken()?.customerNo ?? ""}${operatorID}`, // Fallback — backend slår opp faktisk Profile.ProfileID og overstyrer
        exportedYesNo: correctionData.internalOnly ?? false, // true = intern korreksjon, hopper over ERP-eksport
        lineStatus: 20, // Under arbeid
        itemNo: correctionData.itemNo,
        itemUnitCode: '',
        itemBarCode: null,
        description1: correctionData.description1 || '',
        itemBatchNo: correctionData.itemBatchNo || '',
        itemBatchExpDate: correctionData.itemBatchExpDate,
        itemSerialNo: correctionData.itemSerialNo || '',
        erpLinkName: '',
        shipmentNo: '1', // Standard shipmentNo
        quantityOrigin: 0,
        itemUnitTxt: '',
        lineTypeOrigin: 0, // Standard
        price: 0,
        currency: '',
        reasonCode: correctionData.reasonCode, // VIKTIG: Årsakskode
        transType: correctionData.transType, // VIKTIG: 1=ut av lager, 2=inn på lager
        sscc: '',
        quantityCorrected: correctionData.quantity, // Korrigert antall
        freeField1: null,
        freeField2: null,
        freeField3: null,
        appFreeField1: null,
        appFreeField2: null,
        quantityGross: 0,
        transitPackagingID: 0,
        stcBoxNo: 0,
        grShipmentNo: 0,
        externalSystemID: null,
        externalSystemStatus: null,
        itemType: correctionData.itemType ?? 0,
        orderNoERP: null,
        lineNoERP: 0
      };

      const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans/${batchId}/transactions`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(extractApiError(error, response, 'Failed to add correction line'));
      }
      
      const responseData = await response.json();
      return this.mapToCorrectionLine(responseData);
    } catch (error) {
      console.error('Error adding correction line:', error);
      throw new Error('Kunne ikke legge til korreksjonslinje');
    }
  }

  // Hent korreksjonslinjer for session
  async getCorrectionLines(batchId: string): Promise<InventoryCorrectionLine[]> {
    try {
      const response = await AuthService.authenticatedGet(`${this.baseUrl}/stocktrans/${batchId}/transactions`);
      if (!response.ok) {
        throw new Error('Failed to fetch correction lines');
      }
      const transactions = await response.json();
      
      return transactions
        .filter((t: any) => t.lineType === 3) // Korreksjon (legacy: LineType 3)
        .map((t: any) => this.mapToCorrectionLine(t))
        .sort((a: any, b: any) => new Date(b.transDt).getTime() - new Date(a.transDt).getTime());
    } catch (error) {
      console.error('Error getting correction lines:', error);
      return [];
    }
  }

  // Fullfør korreksjonssession.
  // Ved feil kastes en CompleteCorrectionError med serverens lesbare melding
  // (f.eks. "Korreksjon ville presset 178043-01260/1Q0A05 ...") og strukturert
  // detalj (itemNo/binCode) slik at UI kan henvise til linjen som må rettes/slettes.
  // Tidligere svelget denne feilen og returnerte false, slik at operatøren kun
  // fikk en intetsigende generisk melding.
  async completeCorrectionSession(batchId: string): Promise<boolean> {
    const response = await AuthService.authenticatedPost(`${this.baseUrl}/stocktrans/${batchId}/complete`);
    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      const message = extractApiError(errorBody, response, 'Kunne ikke fullføre korreksjonen');
      console.error('Error completing correction session:', message, errorBody);
      const err = new Error(message) as CompleteCorrectionError;
      err.errorCode = typeof errorBody?.errorCode === 'string' ? errorBody.errorCode : undefined;
      err.details = (errorBody?.details as CorrectionErrorDetails) ?? null;
      throw err;
    }
    return true;
  }

  // Oppdater korreksjonslinje
  async updateCorrectionLine(batchId: string, lineId: number, quantity: number): Promise<InventoryCorrectionLine> {
    try {
      const request = {
        quantity: quantity,
        quantityCorrected: quantity // Oppdater korrigert antall også
      };

      const response = await AuthService.authenticatedPut(`${this.baseUrl}/stocktrans/${batchId}/transactions/${lineId}`, request);
      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(extractApiError(error, response, 'Failed to update correction line'));
      }
      
      const responseData = await response.json();
      return this.mapToCorrectionLine(responseData);
    } catch (error) {
      console.error('Error updating correction line:', error);
      throw new Error('Kunne ikke oppdatere korreksjonslinje');
    }
  }

  // Slett korreksjonslinje
  async deleteCorrectionLine(batchId: string, lineId: number): Promise<boolean> {
    try {
      const response = await AuthService.authenticatedDelete(`${this.baseUrl}/stocktrans/${batchId}/transactions/${lineId}`);
      if (!response.ok) {
        const error = await response.json().catch(() => null);
        throw new Error(extractApiError(error, response, 'Failed to delete correction line'));
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting correction line:', error);
      throw new Error('Kunne ikke slette korreksjonslinje');
    }
  }

  // Helper methods
  private mapToCorrectionSession(details: any): InventoryCorrectionSession {
    return {
      batchID: details.header.batchID,
      operatorID: details.header.operatorID,
      description1: details.header.description1,
      orderStatus: details.header.orderStatus,
      statusText: details.header.statusText,
      warehouse: details.header.warehouse,
      orderDate: new Date(details.header.orderDate),
      transactionCount: details.totalTransactions,
      lines: details.transactions
        .filter((t: any) => t.lineType === 3) // Korreksjon (legacy: LineType 3)
        .map((t: any) => this.mapToCorrectionLine(t))
        .sort((a: any, b: any) => new Date(b.transDt).getTime() - new Date(a.transDt).getTime())
    };
  }

  private mapToCorrectionLine(transaction: any): InventoryCorrectionLine {
    return {
      lineID: transaction.lineID,
      itemNo: transaction.itemNo,
      locationName: transaction.locationName,
      quantity: transaction.quantity,
      warehouse: transaction.warehouse,
      transType: transaction.transType || 1,
      transTypeText: this.getTransTypeText(transaction.transType || 1),
      reasonCode: transaction.reasonCode || 1,
      description1: transaction.description1,
      itemBatchNo: transaction.itemBatchNo,
      itemBatchExpDate: transaction.itemBatchExpDate ? new Date(transaction.itemBatchExpDate) : undefined,
      itemSerialNo: transaction.itemSerialNo,
      transDt: new Date(transaction.transDt),
      lineStatus: transaction.lineStatus,
      statusText: transaction.statusText || 'Under arbeid'
    };
  }

  private getTransTypeText(transType: number): string {
    return transType === 1 ? 'Ut av lager' : 'Inn på lager';
  }

  // Få beskrivelse for årsakskode
  getReasonCodeDescription(reasonCode: number): string {
    const codes = this.getReasonCodes();
    const reason = codes.find(r => r.code === reasonCode);
    return reason ? reason.description : `Kode ${reasonCode}`;
  }
}

export const inventoryCorrectionService = new InventoryCorrectionService();
